import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { WarrantyActivationFormService } from '@app/features/warranties/activate-warranty-process/steps/warranty-activation-form.service';
import { WarrantiesService } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { firstValueFrom } from 'rxjs';
import { SingleScannerComponent } from '@src/app/shared/components/single-scanner/single-scanner.component';

@Component({
    selector: 'app-warranty-code-validation',
    templateUrl: './warranty-code-validation.component.html',
    styleUrl: './warranty-code-validation.component.scss',
    host: {
        class: 'app-warranty-code-validation'
    },
    standalone: false
})
export class StepWarrantyCodeValidationComponent implements OnInit {

  filterOperator = FilterOperator;
  warrantyCode = {};
  warrantyResults: any[] = [];
  isFormSubmitted: boolean = false;
  isResultsEmpty: boolean = true;

  @Output() stepSaved = new EventEmitter<void>();

  isCameraScannerVisible = false;
  scannedQRCode: string | null = null;
  @ViewChild(SingleScannerComponent) cameraScannerComponent?: SingleScannerComponent;

  warrantyCodeValidationFormGroup: FormGroup = this.fb.group({
    warrantyCode: ['', [Validators.required]],
  });

  constructor(
    private fb: FormBuilder,
    private warrantiesService: WarrantiesService,
    public warrantyActivationFormService: WarrantyActivationFormService) { }

  ngOnInit() {
    this.initFormStep();
  }

  initFormStep() {
    const stepData = this.warrantyActivationFormService.getStepData('warrantyCodeData');
    if (stepData) {
      this.warrantyCodeValidationFormGroup.patchValue(stepData);
    }
  }

  openCameraScanner() {
    this.isFormSubmitted = false;
    this.warrantyResults = [];
    this.isResultsEmpty = true;
    this.warrantyCodeValidationFormGroup.patchValue({
      warrantyCode: null
    });
    this.isCameraScannerVisible = true;
  }

  closeCameraScanner() {
    this.cameraScannerComponent?.closeScanner();
    this.isCameraScannerVisible = false;
  }

  onQRCodeScanned(qrCode: string) {
    this.scannedQRCode = qrCode;

    const warrantyCode = this.extractValueFromUrl(qrCode);

    this.warrantyCodeValidationFormGroup.patchValue({
      warrantyCode: warrantyCode
    });
  }

  onScannerClosed() {
    this.isCameraScannerVisible = false;
  }

  private extractValueFromUrl(codeString: string): string {
    const raw = (codeString ?? '').trim();
    if (!raw) return '';

    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      // Not a valid URL
      return raw;
    }

    // /product/of/{company}?w={code}
    const fromQuery = url.searchParams.get('w') ?? url.searchParams.get('code');
    if (fromQuery) {
      return fromQuery.trim();
    }

    const segments = url.pathname
      .split('/')
      .filter(Boolean)
      .map(s => decodeURIComponent(s));
    const first = segments[0]?.toLowerCase();

    // /certificate/{company}/w/{code}
    if (first === 'certificate') {
      const wIndex = segments.findIndex(s => s.toLowerCase() === 'w');
      return wIndex > -1 ? segments[wIndex + 1] ?? '' : '';
    }

    // /warranty/{code}
    if (first === 'warranty') {
      return segments[1] ?? '';
    }

    return raw;
  }

  async onValidateWarrantyCode() {

    this.isCameraScannerVisible = false;

    const filters: any[] = [];
    filters.push({
      propertyName: 'code',
      operator: 'eq',
      values: [this.warrantyCodeValidationFormGroup.controls['warrantyCode'].value]
    });

    filters.push({
      propertyName: 'isActive',
      operator: 'eq',
      values: ['false']
    });

    filters.push({
      propertyName: 'isPrepaired',
      operator: 'eq',
      values: ['false']
    });

    const filter = {
      "pageNumber": 1,
      "pageSize": 1,
      "filters": filters
    };

    try {
      const response = await firstValueFrom(this.warrantiesService.apiWarrantiesFilterPost(filter));
      this.isFormSubmitted = true;

      if (response.results && response.results.length > 0) {
        this.warrantyResults = response.results;
        this.isResultsEmpty = false;
        this.onSaveStepData();
      } else {
        this.warrantyResults = [];
        this.isResultsEmpty = true;
      }
    } catch (error) {
      console.error('Error validating warranty code', error);
    }
  }

  onSaveStepData() {
    this.warrantyActivationFormService.setStepData('warrantyCodeData', this.warrantyCodeValidationFormGroup.value)
    this.stepSaved.emit();
  }
}
